import React from 'react';
import {components} from "react-select";
import {styled} from "@mui/material/styles";

import {ReactComponent as ArrowDown} from "assets/img/arrow-down.svg";
import {ReactComponent as ArrowUp} from "assets/img/arrow-up.svg";
import {ReactComponent as BadgeClose} from "assets/img/badge-close.svg";
import {ReactComponent as Tick} from "assets/img/list-select-tick.svg";
import {black500, blue100, blue500, grey50, grey300, grey500, greyishBlue50, greyishBlue200, greyishBlue400} from "assets/jss/main";
import normalizeForComparison from "utils/normalizeForComparison";

/**
 * Shared pieces of the DS list selects (RadioListSelect, CheckboxListSelect): a 40px
 * control, a menu of 32px options with a marker on the right, a tick on the selected ones.
 */
const VISIBLE_OPTIONS = 6;
const OPTION_HEIGHT = 32;
const LIST_PADDING = 8;

const OptionLabel = styled('span')({
    minWidth: 0,
    overflow: 'hidden',
    whiteSpace: 'nowrap',
    textOverflow: 'ellipsis',
});

const Marker = styled('span')({
    flexShrink: 0,
    width: '16px',
    height: '16px',
    marginLeft: 'auto',
    boxSizing: 'border-box',
    border: `1px solid ${black500}`,
    backgroundColor: grey50,
});

const SelectedMarker = styled(Tick)({
    flexShrink: 0,
    marginLeft: 'auto',
});

export const RadioMarker = styled(Marker)({borderRadius: '24px'});

export const CheckboxMarker = styled(Marker)({borderRadius: '4px'});

export function optionWithMarker(UnselectedMarker) {
    return function Option(props) {
        const {data, isSelected} = props;

        return (
            <components.Option {...props}>
                <OptionLabel title={data.label}>{data.label}</OptionLabel>
                {data.adornment}
                {isSelected ? <SelectedMarker/> : <UnselectedMarker/>}
            </components.Option>
        );
    };
}

export function SingleValue(props) {
    return (
        <components.SingleValue {...props}>
            <OptionLabel>{props.data.label}</OptionLabel>
            {props.data.adornment}
        </components.SingleValue>
    );
}

export function MultiValueLabel(props) {
    return (
        <components.MultiValueLabel {...props}>
            <span style={{display: 'flex', alignItems: 'center', gap: '4px'}}>
                {props.data.adornment}
                {props.children}
            </span>
        </components.MultiValueLabel>
    );
}

export function MultiValueRemove(props) {
    return (
        <components.MultiValueRemove {...props}>
            <BadgeClose/>
        </components.MultiValueRemove>
    );
}

export function DropdownIndicator(props) {
    return (
        <components.DropdownIndicator {...props}>
            {props.selectProps.menuIsOpen ? <ArrowUp/> : <ArrowDown/>}
        </components.DropdownIndicator>
    );
}

export const filterOption = (option, input) => normalizeForComparison(option.label).includes(normalizeForComparison(input));

/**
 * @param {boolean} highlighted blue border while closed, to point at a value still to choose
 * @param {boolean} multi       badges in the control, on one clipped line until the menu opens
 */
export function listSelectStyles(highlighted, multi = false) {
    return {
        control: (base, {isDisabled, menuIsOpen}) => ({
            ...base,
            minHeight: '40px',
            height: multi && menuIsOpen ? 'auto' : '40px',
            padding: multi ? '7px 16px' : '0 16px',
            border: `1px solid ${menuIsOpen || (highlighted && !isDisabled) ? blue500 : greyishBlue200}`,
            borderRadius: '8px',
            boxShadow: menuIsOpen ? `0px 0px 0px 1px ${blue100}` : 'none',
            backgroundColor: isDisabled ? greyishBlue50 : '#fff',
            cursor: isDisabled ? 'default' : 'pointer',
            '&:hover': {borderColor: menuIsOpen || highlighted ? blue500 : greyishBlue200},
        }),
        valueContainer: (base, {selectProps}) => ({
            ...base,
            padding: 0,
            gap: '8px',
            ...(multi && !selectProps.menuIsOpen ? {flexWrap: 'nowrap', overflow: 'hidden'} : {}),
        }),
        singleValue: (base) => ({
            ...base,
            display: 'flex',
            alignItems: 'center',
            gap: '8px',
            margin: 0,
            maxWidth: '100%',
            color: black500,
            fontSize: '14px',
        }),
        multiValue: (base) => ({
            ...base,
            flexShrink: 0,
            alignItems: 'center',
            gap: '4px',
            height: '24px',
            margin: 0,
            padding: '0 4px 0 12px',
            boxSizing: 'border-box',
            border: `1px solid ${grey500}`,
            borderRadius: '4px',
            backgroundColor: grey300,
        }),
        multiValueLabel: (base) => ({...base, padding: 0, paddingLeft: 0, color: black500, fontSize: '12px'}),
        multiValueRemove: (base) => ({
            ...base,
            padding: 0,
            borderRadius: 0,
            cursor: 'pointer',
            '&:hover': {backgroundColor: 'transparent'},
        }),
        placeholder: (base) => ({...base, margin: 0, color: greyishBlue400, fontSize: '14px'}),
        input: (base) => ({...base, margin: 0, padding: 0, color: black500, fontSize: '14px'}),
        indicatorsContainer: (base) => ({...base, alignSelf: multi ? 'flex-start' : 'center', height: multi ? '24px' : 'auto'}),
        dropdownIndicator: (base) => ({
            ...base,
            padding: 0,
            marginLeft: '8px',
            '& svg': {width: '16px', height: '16px'},
        }),
        menuPortal: (base) => ({...base, zIndex: 1300}),
        menu: (base) => ({
            ...base,
            marginTop: '8px',
            overflow: 'hidden',
            border: `1px solid ${greyishBlue200}`,
            borderRadius: '8px',
            boxShadow: '0px 4px 22px 0px rgba(0, 0, 0, 0.04)',
        }),
        menuList: (base) => ({
            ...base,
            maxHeight: `${VISIBLE_OPTIONS * OPTION_HEIGHT + 2 * LIST_PADDING}px`,
            padding: `${LIST_PADDING}px`,
            '&::-webkit-scrollbar': {width: '16px', borderLeft: `1px solid ${grey500}`},
            '&::-webkit-scrollbar-thumb': {
                border: '4px solid transparent',
                borderRadius: '999px',
                backgroundClip: 'padding-box',
                backgroundColor: '#BDBDBD',
            },
        }),
        option: (base, {isSelected, isFocused, isDisabled}) => ({
            ...base,
            display: 'flex',
            alignItems: 'center',
            gap: '8px',
            height: `${OPTION_HEIGHT}px`,
            padding: '8px',
            borderRadius: '8px',
            backgroundColor: isSelected ? blue500 : (isFocused && !isDisabled ? grey300 : '#fff'),
            color: isSelected ? '#fff' : black500,
            opacity: isDisabled ? 0.5 : 1,
            fontSize: '14px',
            cursor: isDisabled ? 'default' : 'pointer',
            '&:active': {backgroundColor: isSelected ? blue500 : grey300},
        }),
        noOptionsMessage: (base) => ({...base, padding: '8px', textAlign: 'left', color: greyishBlue400, fontSize: '14px'}),
    };
}
